import { useAuth } from '../stores/auth';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from 'lucide-react';

export function RequireAuth({ children, roles }) {
  const { user, profile, org, loading, initialized } = useAuth();
  const location = useLocation();

  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="animate-spin text-[var(--color-teal)]" />
      </div>
    );
  }
  if (!user || !profile) return <Navigate to="/login" replace />;

  // -------------------------------------------------------------------------
  // HIPAA Access Gate: block PHI access until the organization completes
  // onboarding (which includes signing the BAA in Step 0).
  // - org_admin → forced into the wizard at /onboarding
  // - therapist → safely held at /onboarding-pending (no PHI rendered)
  // - super_admin and virtual_ops_manager are platform-level and exempt.
  // - Routes under /onboarding are exempt to prevent redirect loops.
  // -------------------------------------------------------------------------
  const onOnboardingRoute = location.pathname.startsWith('/onboarding');
  const role = profile.role;
  const isPlatformRole = role === 'super_admin' || role === 'virtual_ops_manager';
  const orgNeedsOnboarding = !!profile.org_id && org && org.onboarding_completed === false;

  // Therapists are exempt: they can use the app immediately after accepting an invite.
  if (orgNeedsOnboarding && !isPlatformRole && role !== 'therapist' && !onOnboardingRoute) {
    if (role === 'org_admin') return <Navigate to="/onboarding" replace />;
    // Any other org-scoped role defaults to pending
    return <Navigate to="/onboarding-pending" replace />;
  }

  // If a therapist somehow lands on /onboarding (wizard is admin-only), bounce them home
  if (role === 'therapist' && location.pathname === '/onboarding') {
    return <Navigate to="/my-schedule" replace />;
  }

  if (roles && !roles.includes(role)) {
    // Redirect to role's home
    if (role === 'virtual_ops_manager') return <Navigate to="/vom" replace />;
    if (role === 'super_admin') return <Navigate to="/super-admin" replace />;
    if (role === 'therapist') return <Navigate to="/my-schedule" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}
